<template>
  <template v-if="disabled">
    <FriedTooltip :delay="{ show: 300, hide: 100 }" :distance="8">
      <FriedButton
        data-external="topbar-create-document-button"
        disabled
        :class="['create-document-button', disabled]"
      >
        {{ $t('create') }}
      </FriedButton>
      <template #popper>
        {{ $t('create-document-restricted-tooltip') }}
      </template>
    </FriedTooltip>
  </template>
  <FriedButton
    v-else-if="isDealRoomModuleActive"
    compact
    data-external="topbar-create-document-button"
    class="create-dsr-button"
    @click="$emit('create-dsr')"
  >
    {{ $t('create-room') }}
  </FriedButton>
  <FriedButton
    v-else-if="isXSmallScreenAndUp"
    compact
    data-external="topbar-create-document-button"
    class="create-document-button"
    @click="$emit('create-document')"
  >
    {{ $t('create') }}
  </FriedButton>
  <FriedDropdown v-else :main-axis-offset="8">
    <template #trigger="{ open }">
      <FriedButton
        compact
        data-external="topbar-create-document-button"
        class="create-document-button"
        :class="{ active: open }"
      >
        {{ $t('create') }}
      </FriedButton>
    </template>
    <template #content>
      <FriedH4 class="create-document-menu-heading">
        {{ $t('create-your-digital-sales-room') }}
      </FriedH4>
      <FriedDropdownItem
        v-for="item in tabs"
        :id="`create-document-dropdown-item-${item.key}`"
        :key="item.key"
        compact
        :data-external="`create-document-type:${item.key}-button`"
        class="create-document-item"
        @click="$emit('create-document', item.key)"
      >
        <FriedParagraph>{{ item.label }}</FriedParagraph>
        <template #leftIcon>
          <FriedIcon :icon="item.icon" />
        </template>
      </FriedDropdownItem>
    </template>
  </FriedDropdown>
</template>

<script lang="ts" setup>
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import type { IconName } from '@getaccept/fried-tofu';
import { storeToRefs } from 'pinia';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useModulesStore } from '../../modules/store/modules.store';
import type { CreateDocumentSection } from '../../document-create/types/create-document-section';
import { useSessionStore } from '../../session/store/session.store';

defineEmits<{
  'create-dsr': [];
  'create-document': [CreateDocumentSection?];
}>();

const { session } = storeToRefs(useSessionStore());
const disabled = computed(() => session.value?.disallow_create);

const { isDealRoomModuleActive } = storeToRefs(useModulesStore());

const { isXSmallScreenAndUp } = useBreakpoints();

const tabs: ComputedRef<{ key: CreateDocumentSection; label: string; icon: IconName }[]> = computed(
  () => [
    { key: 'template', label: t('use-template'), icon: 'template' },
    { key: 'form', label: t('start-off-fresh'), icon: 'plus-large' },
    { key: 'upload', label: t('upload-document'), icon: 'upload' },
  ]
);
</script>

<style lang="scss" scoped>
.disabled-container {
  display: inline-block;
}

.create-document-menu-heading {
  padding: var(--spacing-50);
}

.create-document-button.button,
.create-dsr-button.button {
  @media only screen and (max-width: calc(#{$xs} - 1px)) {
    height: 2.75rem;
    width: 100%;
  }
}

.create-document-button {
  background: var(--signature-gradient);

  &.disabled {
    background: inherit;
  }

  &:hover:not(.disabled) {
    background: var(--signature-gradient-hover);
  }

  &.active {
    background: var(--signature-gradient-active);

    &:hover {
      background: var(--signature-gradient-active-hover);
    }
  }
}
</style>
